// @tier: PREMIUM
// Controller de la page « Notifications envoyées » (/notifications/sent) : historique des annonces admin.
// État de navigation (page, filtres, annonce ouverte) dans l'URL : le retour navigateur et le
// rafraîchissement conservent la position. Conception : docs/ideas/admin-broadcast-sent-page.md
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import unifiedApi from '../services/unifiedApi';
import { BROADCAST_PERIODS, PAGINATION } from '../config/constants';
import { PRIORITY_VALUES } from '../utils/notificationPriority';

export const NEW_BROADCAST_PATH = '/admin/notifications/new';
export const NOTIFICATIONS_PATH = '/admin/notifications';

const DAY_MS = 24 * 60 * 60 * 1000;
export const PERIODS = [
  { value: '7', label: '7 j', days: BROADCAST_PERIODS.SHORT_DAYS },
  { value: '30', label: '30 j', days: BROADCAST_PERIODS.DEFAULT_DAYS },
  { value: '90', label: '90 j', days: BROADCAST_PERIODS.LONG_DAYS },
  { value: 'all', label: 'Tout', days: null },
];
const DEFAULT_PERIOD = 'all';

/** Début de période ISO pour l'API, undefined pour « Tout ». */
export const periodToStartDate = (period, now = Date.now()) => {
  const days = PERIODS.find((item) => item.value === period)?.days;
  return days ? new Date(now - days * DAY_MS).toISOString() : undefined;
};

const plural = (count, singular, pluralForm) => `${count} ${count > 1 ? pluralForm : singular}`;

export const RECIPIENT_LABELS = { parent: 'Parents', driver: 'Chauffeurs' };
const RECIPIENT_FILTER_VALUES = Object.keys(RECIPIENT_LABELS);

/**
 * « Toute l'école », « Tous les chauffeurs », « 2 circuits », « 1 arrêt », « 12 parents ».
 * En mode personnalisé, `recipientsCount` (recipients_count, figé à l'envoi après filtrage
 * d'éligibilité) prime sur la taille brute de la sélection : sans lui, un admin verrait par
 * exemple « 20 parents » ici et « X / 17 » dans la colonne Lues, sans explication.
 */
export const audiencePart = (audience, recipientType = 'parent', recipientsCount) => {
  const isDriver = recipientType === 'driver';
  if (!audience || audience.mode === 'school') return isDriver ? 'Tous les chauffeurs' : "Toute l'école";
  const selectedCount = audience.items ? audience.items.length : (audience.ids || []).length;
  if (audience.mode === 'routes') return plural(selectedCount, 'circuit', 'circuits');
  if (audience.mode === 'stops') return plural(selectedCount, 'arrêt', 'arrêts');
  const count = recipientsCount ?? selectedCount;
  return isDriver ? plural(count, 'chauffeur', 'chauffeurs') : plural(count, 'parent', 'parents');
};

/** « Parents · Toute l'école », « Chauffeurs · 2 circuits »… */
export const audienceLabel = (audience, recipientType = 'parent', recipientsCount) =>
  `${RECIPIENT_LABELS[recipientType] ?? RECIPIENT_LABELS.parent} · ${audiencePart(audience, recipientType, recipientsCount)}`;

/** Préremplissage du composeur depuis le détail d'une annonce (les circuits/arrêts supprimés sont écartés). */
export const buildPrefill = (detail) => ({
  recipientType: detail.recipient_type || 'parent',
  audience: {
    mode: detail.audience.mode,
    ids: detail.audience.items.filter((item) => !item.deleted).map((item) => item.id),
  },
  title: detail.content.fr.title,
  message: detail.content.fr.message,
  titleEn: detail.content.en?.title || '',
  messageEn: detail.content.en?.message || '',
  titleAr: detail.content.ar?.title || '',
  messageAr: detail.content.ar?.message || '',
  priority: detail.priority,
});

const fetchDetail = async (id) => (await unifiedApi.notifications.getBroadcast(id)).data;

const readPage = (value) => Math.max(1, parseInt(value, 10) || 1);

export const useSentNotifications = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Recherche côté navigateur sur la page affichée (comme la liste des notifications reçues).
  const [searchTerm, setSearchTerm] = useState('');
  const listTopRef = useRef(null);
  const firstRender = useRef(true);

  const page = readPage(searchParams.get('page'));
  const priorityParam = searchParams.get('priority');
  const priority = PRIORITY_VALUES.includes(priorityParam) ? priorityParam : 'all';
  const recipientParam = searchParams.get('recipient');
  const recipient = RECIPIENT_FILTER_VALUES.includes(recipientParam) ? recipientParam : 'all';
  const periodParam = searchParams.get('period');
  const period = PERIODS.some((item) => item.value === periodParam) ? periodParam : DEFAULT_PERIOD;
  const selectedId = searchParams.get('id');

  const updateParams = (changes) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === null || value === undefined || value === '' || value === 'all' || value === 1) next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      },
      { replace: false },
    );

  const listQuery = useQuery({
    queryKey: ['broadcasts', 'list', page, priority, recipient, period],
    placeholderData: keepPreviousData,
    queryFn: () =>
      unifiedApi.notifications.listBroadcasts({
        page,
        limit: PAGINATION.BROADCASTS_LIMIT,
        priority: priority === 'all' ? undefined : priority,
        recipient_type: recipient === 'all' ? undefined : recipient,
        start_date: periodToStartDate(period),
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['broadcasts', 'detail', selectedId],
    enabled: Boolean(selectedId),
    retry: false,
    queryFn: () => fetchDetail(selectedId),
  });

  const filteredItems = useMemo(() => {
    const items = listQuery.data?.data ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => `${item.title} ${item.author || ''}`.toLowerCase().includes(term));
  }, [listQuery.data, searchTerm]);
  const pagination = listQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;

  // Page hors limites (filtre resserré, annonces supprimées) : retour à la dernière page valide.
  useEffect(() => {
    if (pagination && totalPages >= 1 && page > totalPages) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (totalPages === 1) next.delete('page');
          else next.set('page', String(totalPages));
          return next;
        },
        { replace: true },
      );
    }
  }, [pagination, totalPages, page, setSearchParams]);

  // Défilement en haut de la liste à chaque changement de page (pas au premier rendu).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    listTopRef.current?.scrollIntoView?.({ block: 'start' });
  }, [page]);

  // Lien profond vers une annonce inconnue ou d'une autre école : message puis paramètre retiré.
  useEffect(() => {
    if (!selectedId || !detailQuery.isError) return;
    toast.error(
      detailQuery.error?.response?.status === 404 ? 'Annonce introuvable' : "Impossible de charger l'annonce",
    );
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('id');
        return next;
      },
      { replace: true },
    );
  }, [selectedId, detailQuery.isError, detailQuery.error, setSearchParams]);

  const hasActiveFilters = priority !== 'all' || recipient !== 'all' || period !== DEFAULT_PERIOD;

  return {
    items: filteredItems,
    searchTerm,
    setSearchTerm,
    pagination,
    page,
    totalPages,
    loading: listQuery.isPending,
    fetching: listQuery.isFetching,
    error: listQuery.isError ? listQuery.error?.message || 'Erreur de chargement' : null,
    priority,
    recipient,
    period,
    hasActiveFilters,
    setPriority: (value) => updateParams({ priority: value, page: 1 }),
    setRecipient: (value) => updateParams({ recipient: value, page: 1 }),
    setPeriod: (value) => updateParams({ period: value, page: 1 }),
    resetFilters: () => updateParams({ priority: null, recipient: null, period: null, page: 1 }),
    goToPage: (value) => updateParams({ page: value }),
    listTopRef,
    selectedId,
    detail: detailQuery.data,
    detailLoading: Boolean(selectedId) && detailQuery.isPending && !detailQuery.isError,
    openDetail: (id) => updateParams({ id }),
    closeDetail: () => updateParams({ id: null }),
    refresh: () => {
      listQuery.refetch();
      if (selectedId) detailQuery.refetch();
    },
    duplicate: (detail) => navigate(NEW_BROADCAST_PATH, { state: { prefill: buildPrefill(detail) } }),
    // Depuis le menu d'une ligne : le détail (noms, message) n'est pas dans la liste, on le charge d'abord.
    duplicateById: async (id) => {
      try {
        const detail = await queryClient.fetchQuery({
          queryKey: ['broadcasts', 'detail', id],
          queryFn: () => fetchDetail(id),
          staleTime: 0,
        });
        navigate(NEW_BROADCAST_PATH, { state: { prefill: buildPrefill(detail) } });
      } catch {
        toast.error("Impossible de charger l'annonce");
      }
    },
    newBroadcast: () => navigate(NEW_BROADCAST_PATH),
    back: () => navigate(NOTIFICATIONS_PATH),
  };
};

export default useSentNotifications;
